import React, { useState, useEffect, useRef } from 'react'
import { TextButton, useIsMobile, PortalContextProvider, Link, Text } from '@amzn/storm-ui'
import { LinkProp, ImageProp, ActionProp, ActionType, AlertContentProp, WidgetStatus } from '../../common/props'
import { useEmitInitialRender } from '../../utils/hooks'
import { KatalMetricsPublisher } from '../../utils/metrics'
import { Surface, StyledBottomSheet, ContentTextWrapper, Title, MobileTitle, ComponentTooltip, ComponentAnchoredAlert, MobileLinkWrapper, StyledLink } from './style'
import { MediaContainer } from '../../common/components/MediaContainer'
import { logger } from '../../utils/logger'
import { WidgetTypes } from '../../common/constants'
import { logWithTakt } from '../../utils/takt'
import { dismissWidget, checkIsAnchorVisible } from '../../utils/functions'
import { MountLocationType } from '@amzn/intro-widgets-api'

export interface PopoverProps {
  mobile?: boolean
  content?: string
  alertContent?: AlertContentProp[]
  title?: string
  trigger?: JSX.Element | React.MutableRefObject<undefined>
  inlineLink?: LinkProp
  action?: ActionProp
  image?: ImageProp
  align?: 'center' | 'start' | 'end'
  position?: 'top' | 'right' | 'bottom' | 'left'
  anchorId: string
  dataTaktId: string
  mountLocation?: MountLocationType
  widgetStatus?: WidgetStatus
}

const inlineLinkType = 'inlineLink'

export const AnchoredAlert: React.FC<PopoverProps> = ({
  content,
  alertContent,
  title,
  inlineLink,
  action,
  image,
  mobile: mobileProp,
  trigger,
  dataTaktId,
  anchorId,
  align,
  position,
  widgetStatus,
  ...rest
}) => {
  const mobileFromTheme = useIsMobile()
  const mobile = mobileProp || mobileFromTheme
  const name = `Popover${mobile ? '-mobile' : ''}`
  const containsImage = !!image
  const hasContent = !!content || !!title || !!inlineLink
  const portal = document.getElementById('AACChromePortal')
  const portalRef = useRef(portal)
  const anchoredAlertRenderRef = useRef(document.getElementsByTagName('body')[0])
  useEmitInitialRender(name)
  const metricsPublisher = KatalMetricsPublisher.newChildActionPublisherForMethod(name)

  const [open, setOpen] = useState(true)
  const [dismissed, setDismissed] = useState(widgetStatus === WidgetStatus.DISMISSED)

  const [isAnchorVisible, setIsAnchorVisible] = useState(true)
  const [parentChangedVisibility, setParentChangedVisibility] = useState(false)

  useEffect(() => {
    // This MutationObserver is responsible for detecting the children change in the DOM and removing the AnchoredAlert popover when the corresponding anchor is missing
    const observer = new MutationObserver(() => {
      const anchor = document.querySelectorAll('[data-intro-widget-id="' + anchorId + '"]')[0]
      const popover = document.getElementById(anchorId)
      if (!document.body.contains(anchor) && portal?.contains(popover)) {
        popover?.remove()
      }
    })

    observer.observe(document.documentElement, { childList: true, subtree: true })

    return () => {
      if (observer) {
        observer.disconnect()
      }
    }
  }, [])

  useEffect(() => {
    // Checking whether the anchor exists
    const anchor = document.querySelector(`[data-intro-widget-id='${anchorId}']`)

    if (anchor) {
      // If anchor exists - we need to test whether it is visible
      setParentChangedVisibility(false)
      // Once the component is mounted - we need to check whether the anchor is visible
      const isVisible = checkIsAnchorVisible(anchorId, () => {
        setParentChangedVisibility(true)
      })
      setIsAnchorVisible(isVisible)
    }
  }, [anchorId, parentChangedVisibility])

  useEffect(() => {
    metricsPublisher.publishCounterMonitor('open', open ? 1 : 0)
    metricsPublisher.publishCounterMonitor('dismissed', dismissed ? 1 : 0)
  }, [metricsPublisher, dismissed, open])

  const handleClose = () => {
    setOpen(false)
    setDismissed(true)
    if (!trigger) {
      dismissWidget(anchorId)
    } else {
      dismissWidget(anchorId)
    }
    logWithTakt(mobile ? WidgetTypes.AnchoredAlertMobile : WidgetTypes.AnchoredAlertDesktop, { event: 'close' })
  }

  const link = inlineLink
    ? {
        type: inlineLinkType,
        link: inlineLink,
      }
    : action

  const anchoredAlertContent: AlertContentProp[] | undefined = alertContent && alertContent.length > 0 ? alertContent : content ? [{ content: content }] : undefined
  if (!anchoredAlertContent) return null

  const CommonContentText = ({ hasTitle }) => (
    <ContentTextWrapper hasTitle={hasTitle}>
      {anchoredAlertContent.map((content, i) => (
        <Text fontSize={content.fontSize ?? 'base'} key={`anchoredAlertContent${i}`}>
          {content.content}
        </Text>
      ))}
    </ContentTextWrapper>
  )

  if (!isAnchorVisible) {
    return null
  }

  if (mobile) {
    return (
      <>
        <div onClick={() => setOpen(true)}>{trigger}</div>
        <StyledBottomSheet
          {...rest}
          data-takt-id={dataTaktId}
          isOpen={open}
          onClose={handleClose}
          closeButton={{
            type: 'icon',
            label: 'close',
          }}
        >
          <Surface>
            {image && <MediaContainer src={image?.link} alt={image?.altText} hasContent={hasContent} />}
            {title && <MobileTitle type="h5">{title}</MobileTitle>}
            <CommonContentText hasTitle={!!title} />
            {link && link.link && (
              <MobileLinkWrapper>
                <Link
                  data-takt-id={`${dataTaktId}_link`}
                  size="base"
                  href={link.link.link}
                  target={link.type !== inlineLinkType ? '_blank' : ''}
                  external={link.type === ActionType.OPEN_EXTERNAL_ACTION}
                >
                  {link.link.label}
                </Link>
              </MobileLinkWrapper>
            )}
          </Surface>
        </StyledBottomSheet>
      </>
    )
  }

  const commonProps = {
    ...rest,
    type: 'blue' as const,
    position: position || 'top',
    align: align || 'center',
    closeIconPosition: containsImage ? ('topCorner' as const) : ('default' as const),
    trigger: <TextButton>{trigger}</TextButton>,
    onCloseButtonClick: handleClose,
    'data-takt-id': dataTaktId,
    id: anchorId,
  }

  const PopoverChildren = () => (
    <div onClick={(e) => e.stopPropagation()}>
      {containsImage && <MediaContainer src={image?.link} alt={image?.altText} size="mini" hasContent={hasContent} />}
      {title && <Title type="h5">{title}</Title>}
      <CommonContentText hasTitle={!!title} />
      {link && link.link && (
        <StyledLink>
          <Link data-takt-id={`${dataTaktId}_link`} size="base" href={link.link.link} target={link.type !== inlineLinkType ? '_blank' : ''} external={link.type === ActionType.OPEN_EXTERNAL_ACTION}>
            {link.link.label}
          </Link>
        </StyledLink>
      )}
    </div>
  )

  const popover = (
    <ComponentTooltip {...commonProps}>
      <PopoverChildren />
    </ComponentTooltip>
  )

  if (!portalRef?.current) logger.error('AACChromePortal not found when render Popover', { title })

  if (dismissed) {
    if (portalRef?.current) return <PortalContextProvider portalElementRef={portalRef}>{popover}</PortalContextProvider>
    else return popover
  }

  return (
    <ComponentAnchoredAlert isOpen={open} {...commonProps} portalRef={portalRef?.current ? portalRef : undefined} boundaryRef={anchoredAlertRenderRef?.current ? anchoredAlertRenderRef : undefined}>
      <PopoverChildren />
    </ComponentAnchoredAlert>
  )
}
